import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import classes from './Home12Services.module.scss';
import Home12Header from './Home12Header';

const pageUrl = 'https://www.collabo.link/construction';
const pageTitle = '戸建て住宅・店舗・事務所の建築・改修 | 建築事業 | コラボ株式会社';
const pageDescription = '金沢市に拠点を置くコラボ株式会社の建築事業。戸建て住宅の新築・リフォームを中心に、店舗・事務所の改修、部分修繕、メンテナンスまで丁寧に対応します。';

const services = [
    {
        tag: '新築・改修',
        title: '住まいや店舗を、使いやすい空間へ',
        desc: '戸建て住宅の新築・リフォームから、店舗・事務所の改修まで、暮らしや仕事に合う空間づくりをご一緒に考えます。ご希望と予算を伺い、必要な工事を分かりやすく整理します。',
        image: '/react-assets/home12/construction-consultation-v1.jpg',
        icon: '/react-assets/home10/icons/home10-icon-template.png'
    },
    {
        tag: '修繕・メンテナンス',
        title: '気になる部分を、必要なところから',
        desc: '壁や床、建具、設備まわりなど、住まいや建物の不具合、傷みに対応します。全面改修だけでなく、部分的な修繕や小さな工事もお気軽にご相談ください。',
        image: '/react-assets/home12/construction-renovation-v1.jpg',
        icon: '/react-assets/home10/icons/home10-icon-update.png'
    },
    {
        tag: 'ご相談・進捗共有',
        title: '相談から引き渡しまで、分かりやすく',
        desc: '現地確認からお見積り、工事中の進捗まで、写真や資料を使って丁寧にお伝えします。IT事業で培った経験も活かし、担当者と直接やり取りしやすい進め方を大切にしています。',
        image: '/react-assets/home12/construction-progress-v1.jpg',
        icon: '/react-assets/home10/icons/home10-icon-preview.png'
    }
];

const consultationExamples = [
    '戸建て住宅の新築・増改築',
    'キッチン・浴室のリフォーム',
    '外壁・屋根・建具の部分修繕',
    '店舗の新装・内装工事',
    '事務所のレイアウト変更',
    '工事後の不具合・追加工事'
];

const processSteps = [
    'まずはご相談',
    '現地確認・お見積り',
    '内容・日程の調整',
    '施工・引き渡し'
];

const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '建築事業',
    provider: {
        '@type': 'Organization',
        name: 'コラボ株式会社',
        url: 'https://www.collabo.link/'
    },
    serviceType: '戸建て住宅の新築・リフォーム、店舗・事務所の改修、部分修繕、メンテナンス',
    description: pageDescription,
    url: pageUrl
};

const Home12Construction = () => {
    const heroRef = React.useRef(null);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        const handleScroll = () => {
            const yOffset = window.scrollY;
            if (heroRef.current) {
                heroRef.current.style.backgroundPositionY = `${yOffset * 0.35}px`;
            }

            const parallaxEls = document.querySelectorAll('.js-parallax');
            parallaxEls.forEach(el => {
                const speed = parseFloat(el.getAttribute('data-speed')) || 0.15;
                el.style.transform = `translateY(${yOffset * speed}px)`;
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add(classes.visible);
                    obs.unobserve(entry.target);
                }
            });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

        const animEls = document.querySelectorAll(`.${classes.animate}`);
        animEls.forEach(el => observer.observe(el));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    return (
        <div className={classes.root}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content="戸建て新築,住宅リフォーム,店舗改修,事務所改修,建物修繕,コラボ株式会社,石川県,金沢市" />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:image" content="https://www.collabo.link/react-assets/home12/construction-hero-v2.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <Home12Header mode="sticky" />

            <section
                ref={heroRef}
                className={`${classes.pageHero} ${classes.constructionHero}`}
                style={{
                    backgroundImage: "linear-gradient(90deg, rgba(28, 35, 36, 0.78) 0%, rgba(28, 35, 36, 0.48) 52%, rgba(28, 35, 36, 0.12) 100%), url('/react-assets/home12/construction-hero-v2.jpg')",
                    backgroundPosition: 'center'
                }}
            >
                <div className={classes.heroContent}>
                    <div className={`${classes.subLabel} ${classes.animate} ${classes.fadeInUp}`}>建築事業</div>
                    <h1 className={`${classes.heroTitle} ${classes.animate} ${classes.fadeInUp} ${classes.delay1}`}>
                        戸建て住宅や店舗の建築・改修を、<br />
                        身近なところから。
                    </h1>
                    <p className={`${classes.heroDesc} ${classes.animate} ${classes.fadeInUp} ${classes.delay2}`}>
                        新築・リフォームから部分修繕まで、規模にかかわらずご相談ください。<br />
                        ご希望と予算に合わせて、無理のない進め方をご提案します。
                    </p>
                </div>
            </section>

            <section className={classes.consultationBlock}>
                <div className={classes.consultationInner}>
                    <div className={`${classes.consultationLead} ${classes.animate} ${classes.fadeInUp}`}>
                        <div className={classes.consultationLabel}>身近な建築相談窓口</div>
                        <h2>「どこに頼めばいいか分からない」段階から、お話を伺います。</h2>
                        <p>
                            大がかりな計画だけでなく、日々の暮らしや仕事の中で生まれる小さな困りごとにも対応します。
                            工事内容が決まっていなくても、まずは現状をお聞かせください。
                        </p>
                    </div>
                    <ul className={`${classes.consultationList} ${classes.animate} ${classes.fadeInUp} ${classes.delay1}`}>
                        {consultationExamples.map(example => <li key={example}>{example}</li>)}
                    </ul>
                </div>
            </section>

            <section className={`${classes.servicesWrap} ${classes.constructionServices}`}>
                {services.map((svc, idx) => {
                    const isEven = idx % 2 !== 0;
                    return (
                        <div key={svc.tag} className={`${classes.serviceRow} ${isEven ? classes.rowReverse : ''}`}>
                            <div className={`${classes.serviceImgBox} ${classes.animate} ${isEven ? classes.slideInRight : classes.slideInLeft}`}>
                                <div className={classes.imgWrapper}>
                                    <img src={svc.image} alt={svc.title} className="js-parallax" data-speed="0.03" />
                                </div>
                            </div>
                            <div className={`${classes.serviceTextBox} ${classes.animate} ${isEven ? classes.slideInLeft : classes.slideInRight} ${classes.delay1}`}>
                                <div className={classes.svcTag}>
                                    <img src={svc.icon} alt="" />
                                    {svc.tag}
                                </div>
                                <h2 className={classes.svcTitle}>{svc.title}</h2>
                                <p className={classes.svcDesc}>{svc.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </section>

            <section className={classes.processBlock}>
                <div className={`${classes.sectionHeader} ${classes.animate} ${classes.fadeInUp}`}>
                    <h2 className={classes.sectionTitle}>ご相談から引き渡しまで</h2>
                    <p className={classes.sectionSub}>内容と費用をご確認いただきながら、ひとつずつ進めます</p>
                </div>
                <div className={classes.processSteps}>
                    {processSteps.map((step, i) => (
                        <div key={step} className={`${classes.stepBox} ${classes.animate} ${classes.fadeInUp}`} style={{ transitionDelay: `${i * 150}ms` }}>
                            <div className={classes.stepNum}>STEP 0{i + 1}</div>
                            <h3>{step}</h3>
                        </div>
                    ))}
                </div>
            </section>

            <section className={classes.bottomCta}>
                <div className={`${classes.ctaContainer} ${classes.animate} ${classes.zoomIn}`}>
                    <h2>小さな修繕から、戸建て・店舗の工事まで。<br />まずは状況をお聞かせください。</h2>
                    <div className={classes.ctaButtons}>
                        <Link to="/contact" className={classes.btnPrimary}>お問い合わせする</Link>
                        <Link to="/company" className={classes.btnSecondary}>会社情報を見る</Link>
                    </div>
                </div>
            </section>

            <footer className={classes.footer}>
                <div className={classes.slogan}>Practical DX for Every Team</div>
                <div className={classes.footerLinks}>
                    <Link to="/company">運営会社</Link>
                    <Link to="/privacy">プライバシーポリシー</Link>
                    <Link to="/contact">お問い合わせ</Link>
                </div>
                <div className={classes.companyInfo}>
                    <div className={classes.name}>コラボ株式会社</div>
                    <p>
                        〒921-8163<br />
                        石川県金沢市横川７丁目２６<br />
                        TEL: 076-287-0026
                    </p>
                    <p style={{ marginTop: '10px' }}>&copy; {new Date().getFullYear()} collabo Inc.</p>
                </div>
            </footer>
        </div>
    );
};

export default Home12Construction;
